import { CONSTRUCTION_FORM_ROUTE_NAMES } from "@/router/constructionRoutes";

/**
 * Which navigation columns `views/BaseView.vue` shows, as pure functions of the route name.
 *
 * Extracted from that component so it can be tested. The failure mode here is silent: rename a route and
 * the matching column simply stops appearing, with nothing to catch it. That is not hypothetical — the
 * wizard routes sat in the wrong list for one commit, and only a careful read caught it.
 */

/** Project pages that carry a sub-navigation of their own. */
export const PROJECT_SUB_SIDEBAR_ROUTE_NAMES = [
  "projectBreakdown",
  "materialMappingView",
  "transportMapping",
  "wasteSorting",
  // The project's settings, which is where the edit wizard's steps now live: one page under
  // ProjectDetailLayout rather than a wizard with its own column, so it keeps the project's own
  // sub-navigation beside it. The sidebar's Settings row has to survive being used, or it is a
  // launcher that deletes itself.
  "projectSettings",
  // The Speckle integration's screen is the settings page on a second route; same column beside it.
  "projectSettingsSpeckle",
  "projectVersionHistory",
] as const;

export const AGENT_SUB_SIDEBAR_ROUTE_NAMES = ["skills", "agent", "agentUsage", "agentUsageHistory"] as const;

/**
 * Routes with no sub-navigation of their own but dense content that benefits from the main sidebar's
 * width — it collapses to the icon rail here too, it just doesn't reserve a sub-nav column.
 */
export const COLLAPSED_NO_SUBNAV_ROUTE_NAMES = ["createMaterial", ...CONSTRUCTION_FORM_ROUTE_NAMES] as const;

/**
 * Routes that bring their own navigation and take the whole content area with it: `BaseView.vue` shows
 * neither the main sidebar nor any sub-navigation column here.
 *
 * The LCA report is the only one (RTLCA-5435). It is a document, not an app screen — its own left rail
 * lists the report's pages the way the platform's sidebars list sections, and it carries the way out
 * ("Back to dashboard"), so a route on this list must ALWAYS render a navigation of its own including a
 * link back. Without that the user is stranded: there is nothing else on screen to navigate with.
 */
export const OWN_NAVIGATION_ROUTE_NAMES = ["projectLcaPdfReportPage"] as const;

/**
 * Routes that keep the Projects item lit in the main sidebar: the list itself, everything with the project
 * sub-navigation, and both wizards.
 *
 * Derived rather than listed by hand, which is how `createProject`, `cloneProject` and
 * `projectVersionHistory` came to be missing while `editProject` was there: adding a project route to a
 * column list above now lights the nav item too.
 */
export const PROJECT_NAV_ACTIVE_ROUTE_NAMES = ["projects", ...PROJECT_SUB_SIDEBAR_ROUTE_NAMES] as const;

export const ADMIN_NAV_ACTIVE_ROUTE_NAMES = ["adminPanel", "tenantBreakdown"] as const;

const matches = (routeNames: readonly string[], routeName: string | undefined): boolean =>
  routeName !== undefined && routeNames.includes(routeName);

export const isProjectSubSidebarRoute = (routeName: string | undefined): boolean =>
  matches(PROJECT_SUB_SIDEBAR_ROUTE_NAMES, routeName);

export const isAgentSubSidebarRoute = (routeName: string | undefined): boolean =>
  matches(AGENT_SUB_SIDEBAR_ROUTE_NAMES, routeName);

/** The route carries its own navigation, so neither the main sidebar nor a sub-navigation column shows. */
export const isOwnNavigationRoute = (routeName: string | undefined): boolean => matches(OWN_NAVIGATION_ROUTE_NAMES, routeName);

/** The main sidebar keeps its labels only where nothing else has a claim on the width. */
export const isMainSidebarExpandedRoute = (routeName: string | undefined): boolean =>
  !(
    isProjectSubSidebarRoute(routeName) ||
    isAgentSubSidebarRoute(routeName) ||
    matches(COLLAPSED_NO_SUBNAV_ROUTE_NAMES, routeName)
  );
